import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import bookingAssistantAPI from '../services/bookingAssistantAPI';
import { persistAiSearchIntent } from '../utils/applyAssistantIntent';

export const WELCOME_BY_LANG = {
  en: "Hi! Describe your trip or meeting in plain language — I'll find the best stay or room for you.",
  kn: 'ನಮಸ್ಕಾರ! ನಿಮ್ಮ ಪ್ರಯಾಣ ಅಥವಾ ಮೀಟಿಂಗ್ ಅನ್ನು ಸರಳವಾಗಿ ಹೇಳಿ — ನಾನು ಅತ್ಯುತ್ತಮ ಸ್ಟೇ ಅಥವಾ ರೂಮ್ ಹುಡುಕುತ್ತೇನೆ.',
  hi: 'नमस्ते! अपनी यात्रा या मीटिंग सरल भाषा में बताएं — मैं सबसे अच्छा स्टे या रूम ढूंढूंगा।',
  ml: 'ഹായ്! നിങ്ങളുടെ യാത്രയോ മീറ്റിംഗോ ലളിതമായി പറയൂ — ഞാൻ മികച്ച സ്റ്റേയോ റൂമോ കണ്ടെത്താം.',
};

export const useBookingAssistantChat = ({ language = 'en', autoWelcome = true } = {}) => {
  const navigate = useNavigate();
  const welcomeMessage = WELCOME_BY_LANG[language] || WELCOME_BY_LANG.en;
  const [messages, setMessages] = useState(
    autoWelcome ? [{ role: 'assistant', content: welcomeMessage, recommendations: [] }] : []
  );
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const history = useMemo(
    () =>
      messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role, content: m.content })),
    [messages]
  );

  const sendMessage = useCallback(
    async (text, { surpriseMe = false } = {}) => {
      const trimmed = String(text || '').trim();
      if (!trimmed || loading) return null;

      setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
      setLoading(true);

      try {
        const res = await bookingAssistantAPI.chat({
          message: trimmed,
          history: history.slice(-10),
          sessionId,
          language,
          surpriseMe,
          guestKey: bookingAssistantAPI.guestKey(),
        });

        const { reply, recommendations = [], sessionId: sid, intent } = res.data;
        if (sid) setSessionId(sid);

        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: reply, recommendations, intent },
        ]);

        localStorage.setItem(
          'bookingAssistantLastSession',
          JSON.stringify({ sessionId: sid || sessionId, updatedAt: Date.now() })
        );

        return res.data;
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content:
              err.response?.data?.message ||
              'Sorry, I had trouble processing that. Please try again.',
            recommendations: [],
          },
        ]);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [history, language, loading, sessionId]
  );

  const handleBook = useCallback(
    (property, intent) => {
      if (property.bookingType === 'meeting') {
        if (property.isExternal && property.externalLink) {
          window.open(property.externalLink, '_blank', 'noopener,noreferrer');
          return;
        }
        const isDbRoom = /^[a-f\d]{24}$/i.test(String(property.id || ''));
        if (isDbRoom) {
          navigate(`/meetings/room/${property.id}`);
          return;
        }
        navigate(
          `/meetings/search-results?location=${encodeURIComponent(property.city || intent?.destination || '')}`
        );
        return;
      }
      const params = new URLSearchParams();
      if (intent?.checkIn) params.set('checkIn', intent.checkIn);
      if (intent?.checkOut) params.set('checkOut', intent.checkOut);
      if (intent?.guests?.adults) params.set('adults', String(intent.guests.adults));
      const qs = params.toString();
      navigate(`/property/${property.id}${qs ? `?${qs}` : ''}`);
    },
    [navigate]
  );

  const goToSearchWithIntent = useCallback(
    (intent, reply = '', recommendations = []) => {
      persistAiSearchIntent({ intent, reply, recommendations, updatedAt: Date.now() });
      const destination = intent?.destination || intent?.location || '';
      if (intent?.bookingType === 'meeting') {
        navigate(
          `/meetings/search-results?location=${encodeURIComponent(destination)}`
        );
        return;
      }
      navigate(destination ? `/search?location=${encodeURIComponent(destination)}` : '/search');
    },
    [navigate]
  );

  const resetChat = useCallback(() => {
    setMessages([{ role: 'assistant', content: welcomeMessage, recommendations: [] }]);
    setSessionId(null);
  }, [welcomeMessage]);

  useEffect(() => {
    if (!autoWelcome) return;
    const welcome = WELCOME_BY_LANG[language] || WELCOME_BY_LANG.en;
    setMessages((prev) => {
      if (prev.length === 1 && prev[0].role === 'assistant' && !prev[0].intent) {
        return [{ ...prev[0], content: welcome }];
      }
      return prev;
    });
  }, [language, autoWelcome]);

  return {
    messages,
    loading,
    sessionId,
    sendMessage,
    handleBook,
    goToSearchWithIntent,
    resetChat,
    setMessages,
  };
};

export default useBookingAssistantChat;
